import { css } from '@emotion/react'
import { Component } from 'react'
import { inject, observer } from 'mobx-react'
import styled from '@emotion/styled'

import { MinusIcon, PlusIcon, Slider } from '../../../components'
import { MOBILE } from '../../../guided-toolbox/utils/breakpoints'

// Keep the expand toggle off the Restore button once they stack on mobile.
const Container = styled.div`
  ${MOBILE} {
    margin-top: 20px;
  }
`

const IconContainer = styled.span`
  display: inline-block;
  width: 40px;
  height: 24px;
  padding: 0 8px;
`

const SubLabel = styled.div`
  color: ${p => p.theme.lightestGray};
  font-size: 0.75rem;
  padding: 4px 0 4px 15px;
  line-height: 1.4;
`

class RiskCompare extends Component {
  state = { active: false }

  toggleActive = () => this.setState({ active: !this.state.active })

  render() {
    const {
      baseCase,
      institutionalAccount,
      otherInvestmentAccounts,
      features,
      config,
      person: { displayName },
    } = this.props.store
    const { values } = this.props

    if (!institutionalAccount?.balance && otherInvestmentAccounts?.length === 0) {
      return null
    }

    // `baseCase` is a declared maybeNull and every risk marker below reads through it,
    // so render nothing until it loads rather than throw.
    if (!baseCase) {
      return null
    }

    // With no other investment accounts, the advised institutional account is every
    // investment account, so both markers describe the same portfolio and have to sit
    // at the same spot on the scale.
    const allInvestmentsRisk =
      institutionalAccount?.balance && otherInvestmentAccounts?.length === 0
        ? (baseCase.riskLevel ?? baseCase.riskLevelAll)
        : baseCase.riskLevelAll

    return (
      <Container>
        <div
          css={css`
            display: flex;
            align-items: center;
            cursor: pointer;
            & svg {
              fill: #7a8e96;
              transition:
                color 0.3s,
                fill 0.3s;
            }
            &:hover {
              & svg {
                fill: #053240;
              }
            }
            &:active {
              & svg {
                fill: #0495bf;
              }
            }
          `}
          onClick={this.toggleActive}>
          <IconContainer>{this.state.active ? <MinusIcon /> : <PlusIcon />}</IconContainer>
          <span
            css={css`
              font-size: 14px;
              color: #677b84;
            `}>
            Compare with{' '}
            <span
              css={css`
                font-weight: bold;
              `}>
              current risk
            </span>
          </span>
        </div>
        {this.state.active && (
          <FieldContainer>
            <InputBox />
            <SliderBox>
              <SliderTextContainer
                css={
                  !institutionalAccount?.balance &&
                  css`
                    display: none;
                  `
                }>
                <CurrentEmployerPlanLevel risk={baseCase.riskLevel} />
                <CurrentEmployerPlanLevelText risk={baseCase.riskLevel}>
                  {displayName}'s investment plan account with {config.template.sponsorName}
                  {features.outsideAdviceEnabled ? '*' : ''}
                </CurrentEmployerPlanLevelText>
              </SliderTextContainer>
              <Slider
                min={101}
                max={600}
                value={0}
                onChange={() => {}}
                step={1}
                minRisk={values.recomMinRiskLevel}
                maxRisk={values.recomMaxRiskLevel}
              />
              <SliderTextContainer>
                <AllInvestmentsLevel risk={allInvestmentsRisk} />
                <AllInvestmentsLevelText risk={allInvestmentsRisk}>
                  All investment accounts
                </AllInvestmentsLevelText>
              </SliderTextContainer>
              {features.outsideAdviceEnabled && Boolean(institutionalAccount?.balance) && (
                <SubLabel>*And any other accounts included for investment advice</SubLabel>
              )}
            </SliderBox>
          </FieldContainer>
        )}
      </Container>
    )
  }
}

const FieldContainer = styled.div`
  display: flex;
  padding: 12px 0 0;
  @media (max-width: 720px) {
    flex-wrap: wrap;
  }
`

const InputBox = styled.div`
  flex: 0 1 300px;
  visibility: hidden;
  @media (max-width: 720px) {
    flex: 0 1 100%;
    display: none;
  }
`

const SliderBox = styled.div`
  position: relative;
  flex: 1 1 400px;
  padding: 0 8px;
  @media (max-width: 720px) {
    flex: 0 1 100%;
  }
  & > div:nth-of-type(2) {
    & > div:first-of-type {
      & > div:first-of-type {
        display: none;
      }
    }
  }
`

const SliderTextContainer = styled.div`
  margin: 0 15px;
  position: relative;
  height: 48px;
`

const CurrentEmployerPlanLevel = styled.div`
  background-color: #7a8e96;
  border: 2px solid #7a8e96;
  width: 4px;
  height: 20px;
  position: absolute;
  top: 44px;
  z-index: 1;
  left: calc(${p => (p.risk ? (p.risk - 101) / 5 : 0)}% - 2px);
`

const CurrentEmployerPlanLevelText = styled.div`
  position: absolute;
  white-space: nowrap;
  font-size: 14px;
  color: #7a8e96;
  top: 20px;
  left: ${p => (p.risk ? (p.risk - 101) / 5 : 0)}%;
  transform: ${p =>
    ((p.risk ? (p.risk - 101) / 5 : 0) < 10 && 'translateX(0%)') ||
    ((p.risk ? (p.risk - 101) / 5 : 0) > 90 && 'translateX(-100%)') ||
    'translateX(-50%)'};
`

const AllInvestmentsLevel = styled.div`
  background-color: #7a8e96;
  position: absolute;
  border: 2px solid #7a8e96;
  width: 4px;
  height: 20px;
  top: -16px;
  z-index: 1;
  left: calc(${p => (p.risk ? (p.risk - 101) / 5 : 0)}% - 2px);
`

const AllInvestmentsLevelText = styled.div`
  position: absolute;
  white-space: nowrap;
  font-size: 14px;
  color: #7a8e96;
  top: 10px;
  left: ${p => (p.risk ? (p.risk - 101) / 5 : 0)}%;
  transform: ${p =>
    ((p.risk ? (p.risk - 101) / 5 : 0) < 10 && 'translateX(0%)') ||
    ((p.risk ? (p.risk - 101) / 5 : 0) > 90 && 'translateX(-100%)') ||
    'translateX(-50%)'};
`

export default inject('store')(observer(RiskCompare))
